import type {
  MobileGraphqlEndpoint,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { buildArtifactListQuery, type BuiltArtifactQuery } from './build-query';
import type { ResolvedArtifactColumns } from './columns';

/**
 * Build a query for one artifact row by id.
 *
 * mobile-service exposes no singular-by-id root query, so this is the list query
 * constrained to a single id and a page of one. That keeps the selection set,
 * literal escaping, and nested handling in {@link buildArtifactListQuery} rather
 * than duplicating them for a second shape.
 *
 * The id is filtered under `idFilterField`, not the field it was read back from:
 * those two names diverge on Magnet, and using the readable one gets the whole
 * document rejected. See `pickIdFilterField` in `columns.ts`.
 */
export function buildArtifactRecordQuery(
  resolved: ResolvedArtifactColumns,
  endpoint: MobileGraphqlEndpoint,
  uiConfig: MobileUiConfigResponse,
  args: { mobileAcquisitionId: number; recordId: string },
): BuiltArtifactQuery | null {
  // Ids are integers on every artifact type; a non-numeric id means stale or
  // hand-edited URL state, and filtering on it would be a type error server-side.
  const numericId = Number(args.recordId);
  if (!Number.isFinite(numericId)) return null;
  // A type whose `where` input has no row-id key can't be fetched a row at a
  // time at all, so there is no query to build.
  if (!resolved.idFilterField) return null;

  return buildArtifactListQuery(resolved, endpoint, uiConfig, {
    mobileAcquisitionId: args.mobileAcquisitionId,
    offset: 0,
    limit: 1,
    where: { [resolved.idFilterField]: { equals: numericId } },
  });
}
